import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Loader2, LogIn, HeartPulse, MailCheck } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SupplierLogin = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { loginWithToken } = useAuth();
  const token = params.get('token');

  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState(null);
  const [reqEmail, setReqEmail] = useState('');
  const [reqSent, setReqSent] = useState(false);
  const [requesting, setRequesting] = useState(false);

  useEffect(() => { if (!token) setError('This sign-in link is missing or invalid.'); }, [token]);

  const signIn = async () => {
    if (!token) return;
    setVerifying(true);
    setError(null);
    try {
      const { data } = await axios.post(`${API}/supplier-auth/verify`, { token });
      await loginWithToken(data.access_token, data.user);
      navigate(data.user?.role === 'supplier' ? '/portal' : '/admin', { replace: true });
    } catch (e) {
      setError('This sign-in link is invalid or has expired. Request a new one below.');
    } finally {
      setVerifying(false);
    }
  };

  const requestNew = async () => {
    if (!reqEmail || !reqEmail.includes('@')) { setError('Enter a valid email'); return; }
    setRequesting(true);
    try {
      await axios.post(`${API}/supplier-auth/request`, { email: reqEmail.trim() });
      setReqSent(true);
    } catch (e) { /* generic */ setReqSent(true); }
    finally { setRequesting(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4" data-testid="supplier-login-page">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-11 h-11 rounded-xl bg-[#00aeef]/10 flex items-center justify-center">
            <HeartPulse className="w-6 h-6 text-[#00aeef]" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-900">Care Tech Compare</h1>
            <p className="text-xs text-gray-500">Supplier sign-in</p>
          </div>
        </div>

        {!error && token && (
          <>
            <p className="text-sm text-gray-600 mb-6">
              Click below to sign in to your workspace. This link can be used once.
            </p>
            <Button
              className="w-full bg-[#00aeef] hover:bg-[#0098d1]"
              onClick={signIn}
              disabled={verifying}
              data-testid="supplier-signin-btn"
            >
              {verifying ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <LogIn className="w-4 h-4 mr-2" />}
              Sign in
            </Button>
          </>
        )}

        {error && !reqSent && (
          <div data-testid="supplier-login-error">
            <p className="text-sm text-rose-600 mb-4">{error}</p>
            <label className="text-xs font-medium text-gray-600">Email a new sign-in link</label>
            <Input
              type="email"
              placeholder="you@company.com"
              value={reqEmail}
              onChange={(e) => setReqEmail(e.target.value)}
              className="mt-1 mb-3"
              data-testid="supplier-request-email"
            />
            <Button
              variant="outline"
              className="w-full"
              onClick={requestNew}
              disabled={requesting}
              data-testid="supplier-request-btn"
            >
              {requesting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <MailCheck className="w-4 h-4 mr-2" />}
              Send me a link
            </Button>
          </div>
        )}

        {reqSent && (
          <div className="text-center py-4" data-testid="supplier-request-sent">
            <MailCheck className="w-10 h-10 text-emerald-500 mx-auto mb-3" />
            <p className="text-sm text-gray-700">If an account exists for that email, a fresh sign-in link is on its way.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SupplierLogin;
